import { onMount, onDestroy, onTrack } from "@/modules/_";
import { Resize } from "@lib/subs";
import { Scroll } from "@lib/scroll";

interface ParallaxConfig {
  x?: number;
  y?: number;
  scale?: number;
  rotate?: number;
  opacity?: number; // delta to apply over progress (added to initial opacity)
  clip?: number; // uniform inset percentage for all sides
  clipTop?: number; // inset percentage for top
  clipRight?: number; // inset percentage for right
  clipBottom?: number; // inset percentage for bottom
  clipLeft?: number; // inset percentage for left
  clipFromInitial?: boolean; // when true, animate existing inset() towards 0 on all sides
  bounds?: [number, number];
  top?: "top" | "center" | "bottom"; // viewport anchor for when tracking starts
  bottom?: "top" | "center" | "bottom"; // viewport anchor for when tracking ends
}

interface ParallaxInstance {
  element: HTMLElement;
  config: ParallaxConfig;
  track?: ReturnType<typeof onTrack>;
  scrollUnsubscribe?: () => void;
  initialTransform: string; // inline transform value for cleanup
  computedBaseTransform: string; // computed transform at mount used as baseline for deltas
  lastTransform?: string; // Cache last applied transform to avoid unnecessary updates
  elementHeight: number;
  elementWidth: number;
  resizeUnsubscribe?: () => void;
  isHero: boolean;
  initialOpacity: string; // inline opacity value for cleanup
  computedBaseOpacity: string; // computed opacity at mount used as baseline for deltas
  lastOpacity?: string;
  initialClipPath: string;
  lastClipPath?: string;
  initialClipSides?: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
  baselineProgress?: number; // for non-hero elements, progress value at mount/resize
  baselineScroll?: number; // for hero elements, scroll value at mount/resize
  isFrozen?: boolean;
  frozenTransform?: string;
  frozenOpacity?: string;
  frozenClipPath?: string;
}

const instances: ParallaxInstance[] = [];

export default function (element: HTMLElement) {
  // console.log("parallax", element);
  let parallaxInstance: ParallaxInstance | null = null;

  onMount(() => {
    // Parse parallax configuration from data attribute
    const parallaxValue = element.getAttribute("data-parallax");
    if (!parallaxValue) return;

    const config = parseParallaxConfig(parallaxValue);

    // Store initial inline styles for cleanup
    const initialTransform = element.style.transform || "";
    const initialOpacity = element.style.opacity || ""; // empty string means default (1)
    const initialClipPath = element.style.clipPath || "";

    // Capture computed baseline styles (preserve CSS-defined defaults)
    const computedStyle = getComputedStyle(element);
    const computedBaseTransform = computedStyle.transform || "";
    const computedBaseOpacity = computedStyle.opacity || "";

    // Add will-change for GPU acceleration and style hints
    element.style.willChange = "transform, opacity, clip-path";

    // Get element dimensions for more accurate parallax calculations
    const elementRect = element.getBoundingClientRect();
    const elementHeight = elementRect.height;
    const elementWidth = elementRect.width;

    const isHero = element.getAttribute("data-parallax-hero") === "true";

    // For hero elements, use direct scroll listener instead of tracking
    let track: ReturnType<typeof onTrack> | undefined;
    let scrollUnsubscribe: (() => void) | undefined;

    if (isHero) {
      // Hero elements get direct scroll updates without bounds checking
      scrollUnsubscribe = Scroll.add((data: any) => {
        if (parallaxInstance && !parallaxInstance.isFrozen) {
          // For heroes, we don't use progress - just pass 0 as it's not used
          updateParallaxTransform(parallaxInstance, 0, data?.scroll);
        }
      });
    } else {
      // Regular elements use the tracking system
      track = onTrack(element, {
        bounds: config.bounds || [0, 1],
        top: (config.top as any) || "bottom", // Start when element bottom hits viewport top by default
        bottom: (config.bottom as any) || "top", // Stop when element top hits viewport bottom by default
        callback: (progress) => {
          // if (element.classList.contains("home_work_heading_wrap")) {
          // console.log(progress);
          // }

          // Update continuously to prevent jumps at viewport edge
          if (parallaxInstance && !parallaxInstance.isFrozen) {
            updateParallaxTransform(parallaxInstance, progress);
          }
        },
      });
    }

    // Store instance
    // Parse computed clip-path in case it's set via CSS, to support clipFromInitial
    const computedClipPath = computedStyle.clipPath || initialClipPath || "";
    const initialClipSides = parseInsetClipToPercents(computedClipPath, elementWidth, elementHeight);

    // Capture baseline values so deltas start at 0 for in-view elements
    const baselineScroll = (Scroll.scroll ?? window.scrollY) as number;
    const baselineProgress = (track ? track.value : 0) as number;

    parallaxInstance = {
      element,
      config,
      track,
      scrollUnsubscribe,
      initialTransform,
      computedBaseTransform,
      initialOpacity,
      computedBaseOpacity,
      initialClipPath,
      elementHeight,
      elementWidth,
      isHero,
      initialClipSides,
      baselineScroll: isHero ? baselineScroll : undefined,
      baselineProgress: !isHero ? baselineProgress : undefined,
    };

    instances.push(parallaxInstance);

    // Apply first frame using zero-delta baselines
    if (parallaxInstance) {
      if (isHero) {
        updateParallaxTransform(parallaxInstance, 0);
      } else if (track) {
        updateParallaxTransform(parallaxInstance, track.value);
      }
    }

    // Handle resize to update element dimensions
    const resizeUnsubscribe = Resize.add(() => {
      if (parallaxInstance && !parallaxInstance.isFrozen) {
        const newRect = element.getBoundingClientRect();
        parallaxInstance.elementHeight = newRect.height;
        parallaxInstance.elementWidth = newRect.width;
        // Recompute baseline styles to avoid jumps after layout shifts
        const cs = getComputedStyle(element);
        parallaxInstance.computedBaseTransform = cs.transform || "";
        parallaxInstance.computedBaseOpacity = cs.opacity || "";
        // Recompute initial clip sides in case clip-path is defined in px and size changed
        const recomputedClip = cs.clipPath || parallaxInstance.initialClipPath || "";
        parallaxInstance.initialClipSides = parseInsetClipToPercents(recomputedClip, parallaxInstance.elementWidth, parallaxInstance.elementHeight);
        // Re-baseline scroll/progress at resize moment to keep deltas smooth
        if (parallaxInstance.isHero) {
          parallaxInstance.baselineScroll = Scroll.scroll ?? window.scrollY;
        } else if (parallaxInstance.track) {
          parallaxInstance.baselineProgress = parallaxInstance.track.value;
        }
        // Immediately re-apply current frame using latest baseline
        if (parallaxInstance.isHero) {
          updateParallaxTransform(parallaxInstance, 0);
        } else if (parallaxInstance.track) {
          updateParallaxTransform(parallaxInstance, parallaxInstance.track.value);
        }
      }
    });

    // Store unsubscribe function on instance for cleanup
    parallaxInstance.resizeUnsubscribe = resizeUnsubscribe;
  });

  onDestroy(() => {
    if (parallaxInstance) {
      // Remove this instance from the global instances array
      const index = instances.findIndex((instance) => instance === parallaxInstance);
      if (index > -1) {
        instances.splice(index, 1);
      }

      // Remove will-change property
      element.style.willChange = "";

      // Reset transform
      element.style.transform = parallaxInstance.initialTransform;

      // Reset opacity and clip-path
      element.style.opacity = parallaxInstance.initialOpacity;
      element.style.clipPath = parallaxInstance.initialClipPath;

      // Cleanup resize listener
      if (parallaxInstance.resizeUnsubscribe) {
        parallaxInstance.resizeUnsubscribe();
      }

      // Cleanup scroll listener for hero elements
      if (parallaxInstance.scrollUnsubscribe) {
        parallaxInstance.scrollUnsubscribe();
      }

      parallaxInstance = null;
    }

    // Cleanup instances array
  });
}

export function freezeParallax() {
  if (instances.length < 1) return;
  instances.forEach((inst) => {
    if (inst.isFrozen) return;
    inst.isFrozen = true;
    // Snapshot current applied styles
    inst.frozenTransform = inst.element.style.transform;
    inst.frozenOpacity = inst.element.style.opacity;
    inst.frozenClipPath = inst.element.style.clipPath;
  });
}

export function unfreezeParallax() {
  if (instances.length < 1) return;
  instances.forEach((inst) => {
    if (!inst.isFrozen) return;
    inst.isFrozen = false;
    // Re-baseline after unfreeze so deltas continue smoothly from the frozen state
    const cs = getComputedStyle(inst.element);
    inst.computedBaseTransform = cs.transform || inst.computedBaseTransform || "";
    inst.computedBaseOpacity = cs.opacity || inst.computedBaseOpacity || "";
    if (inst.isHero) {
      inst.baselineScroll = Scroll.scroll ?? window.scrollY;
      updateParallaxTransform(inst, 0);
    } else if (inst.track) {
      inst.baselineProgress = inst.track.value;
      updateParallaxTransform(inst, inst.track.value);
    }
  });
}

function parseParallaxConfig(value: string): ParallaxConfig {
  const config: ParallaxConfig = {};

  // Handle simple numeric value (default to y-axis parallax)
  if (/^-?\d*\.?\d+$/.test(value.trim())) {
    config.y = parseFloat(value.trim());
    return config;
  }

  // Handle complex configuration: "y:0.5,x:0.3,scale:0.1,rotate:45"
  const parts = value.split(",").map((part) => part.trim());

  parts.forEach((part) => {
    const [key, val] = part.split(":").map((s) => s.trim());
    const numValue = parseFloat(val);

    switch (key.toLowerCase()) {
      case "x":
        if (!isNaN(numValue)) config.x = numValue;
        break;
      case "y":
        if (!isNaN(numValue)) config.y = numValue;
        break;
      case "scale":
        if (!isNaN(numValue)) config.scale = numValue;
        break;
      case "rotate":
        if (!isNaN(numValue)) config.rotate = numValue;
        break;
      case "opacity":
        if (!isNaN(numValue)) config.opacity = numValue;
        break;
      case "clip":
        if (!isNaN(numValue)) config.clip = numValue;
        break;
      case "cliptop":
      case "clip-top":
        if (!isNaN(numValue)) config.clipTop = numValue;
        break;
      case "clipright":
      case "clip-right":
        if (!isNaN(numValue)) config.clipRight = numValue;
        break;
      case "clipbottom":
      case "clip-bottom":
        if (!isNaN(numValue)) config.clipBottom = numValue;
        break;
      case "clipleft":
      case "clip-left":
        if (!isNaN(numValue)) config.clipLeft = numValue;
        break;
      case "clipfrominitial":
      case "clip-from-initial":
        config.clipFromInitial = val === "true";
        break;
      case "start": {
        if (!isNaN(numValue)) {
          config.bounds = [numValue, config.bounds?.[1] ?? 1];
        }
        break;
      }
      case "end": {
        if (!isNaN(numValue)) {
          config.bounds = [config.bounds?.[0] ?? 0, numValue];
        }
        break;
      }
    }
  });

  return config;
}

function updateParallaxTransform(instance: ParallaxInstance, progress: number, rawScroll?: number) {
  const { element, config, computedBaseTransform, elementHeight, elementWidth, computedBaseOpacity, initialClipPath } = instance;

  // Check if this is a hero element
  const isHero = element.getAttribute("data-parallax-hero") === "true";

  // For hero elements, always use scroll distance directly
  // For non-hero elements, use the progress-based calculation
  let scrollDistance;

  if (isHero) {
    const baseline = instance.baselineScroll ?? 0;
    const current = (rawScroll ?? Scroll.scroll ?? window.scrollY) as number;
    scrollDistance = current - baseline;
  } else {
    const viewportHeight = window.innerHeight;
    const scrollRange = viewportHeight + elementHeight;
    const baseline = instance.baselineProgress ?? 0;
    const deltaProgress = progress - baseline;
    scrollDistance = deltaProgress * scrollRange;
  }

  // Calculate transform values
  let translateX = 0;
  let translateY = 0;
  let translateZ = 0; // Always include for GPU acceleration
  let scaleValue = 1;
  let rotateValue = 0;
  let opacityValue: number | undefined;
  let clipPathValue: string | undefined;

  // Apply computed baseline transform parsing if it exists
  if (computedBaseTransform) {
    // Parse existing transform values to avoid conflicts
    const existing = parseExistingTransform(computedBaseTransform);
    translateX += existing.x || 0;
    translateY += existing.y || 0;
    translateZ += existing.z || 0;
    scaleValue *= existing.scale || 1;
    rotateValue += existing.rotate || 0;
  }

  // Calculate parallax offsets
  if (config.x !== undefined) {
    translateX += config.x * scrollDistance;
  }

  if (config.y !== undefined) {
    translateY += config.y * scrollDistance;
  }

  if (config.scale !== undefined) {
    // For heroes, use direct scroll scaling; for others, use progress-based
    const scaleFactor = isHero ? (rawScroll ?? Scroll.scroll ?? window.scrollY) / window.innerHeight : progress;
    scaleValue *= 1 + config.scale * scaleFactor;
  }

  if (config.rotate !== undefined) {
    // For heroes, use direct scroll rotation; for others, use progress-based
    const rotateFactor = isHero ? (rawScroll ?? Scroll.scroll ?? window.scrollY) / window.innerHeight : progress;
    rotateValue += config.rotate * rotateFactor * 360; // Full rotation = 360deg
  }

  // Generate transform string
  const newTransform = `translate3d(${translateX.toFixed(2)}px, ${translateY.toFixed(2)}px, ${translateZ}px) scale(${scaleValue.toFixed(4)}) rotate(${rotateValue.toFixed(2)}deg)`;

  // Only apply if transform has actually changed (performance optimization)
  if (instance.lastTransform !== newTransform) {
    element.style.transform = newTransform;
    instance.lastTransform = newTransform;
  }

  // Opacity handling: treat config.opacity as delta from initial over progress (0..1)
  if (config.opacity !== undefined) {
    const startOpacity = computedBaseOpacity === "" ? 1 : parseFloat(computedBaseOpacity);
    const factor = isHero ? (Scroll.scroll || window.scrollY) / window.innerHeight : progress;
    opacityValue = Math.max(0, Math.min(1, startOpacity + config.opacity * factor));
    const newOpacityStr = String(opacityValue);
    if (instance.lastOpacity !== newOpacityStr) {
      element.style.opacity = newOpacityStr;
      instance.lastOpacity = newOpacityStr;
    }
  }

  // Clip-path handling: support inset() with percentages; values represent how much to clip in
  if (config.clip !== undefined || config.clipTop !== undefined || config.clipRight !== undefined || config.clipBottom !== undefined || config.clipLeft !== undefined || config.clipFromInitial) {
    const factor = isHero ? (rawScroll ?? Scroll.scroll ?? window.scrollY) / window.innerHeight : progress;
    if (config.clipFromInitial && instance.initialClipSides) {
      const start = instance.initialClipSides;
      const top = (start.top * (1 - factor)).toFixed(2);
      const right = (start.right * (1 - factor)).toFixed(2);
      const bottom = (start.bottom * (1 - factor)).toFixed(2);
      const left = (start.left * (1 - factor)).toFixed(2);
      clipPathValue = `inset(${top}% ${right}% ${bottom}% ${left}%)`;
    } else {
      const top = ((config.clipTop ?? config.clip ?? 0) * factor).toFixed(2);
      const right = ((config.clipRight ?? config.clip ?? 0) * factor).toFixed(2);
      const bottom = ((config.clipBottom ?? config.clip ?? 0) * factor).toFixed(2);
      const left = ((config.clipLeft ?? config.clip ?? 0) * factor).toFixed(2);
      clipPathValue = `inset(${top}% ${right}% ${bottom}% ${left}%)`;
    }
    if (instance.lastClipPath !== clipPathValue) {
      element.style.clipPath = clipPathValue;
      instance.lastClipPath = clipPathValue;
    }
  }
}

function parseExistingTransform(transform: string): {
  x?: number;
  y?: number;
  z?: number;
  scale?: number;
  rotate?: number;
} {
  const result: any = {};

  if (!transform || transform === "none") return result;

  // Parse translate3d or translateX/Y values
  const translate3dMatch = transform.match(/translate3d\(([^)]+)\)/);
  if (translate3dMatch) {
    const values = translate3dMatch[1].split(",").map((v) => parseFloat(v.trim()));
    result.x = values[0] || 0;
    result.y = values[1] || 0;
    result.z = values[2] || 0;
  } else {
    const translateXMatch = transform.match(/translateX\(([^)]+)\)/);
    if (translateXMatch) result.x = parseFloat(translateXMatch[1]);

    const translateYMatch = transform.match(/translateY\(([^)]+)\)/);
    if (translateYMatch) result.y = parseFloat(translateYMatch[1]);
  }

  // Parse scale
  const scaleMatch = transform.match(/scale\(([^)]+)\)/);
  if (scaleMatch) result.scale = parseFloat(scaleMatch[1]);

  // Parse rotate
  const rotateMatch = transform.match(/rotate\(([^)]+)deg\)/);
  if (rotateMatch) result.rotate = parseFloat(rotateMatch[1]);

  // Fallback: matrix(...) or matrix3d(...)
  // matrix(a, b, c, d, tx, ty)
  const matrixMatch = transform.match(/matrix\(([^)]+)\)/);
  if (matrixMatch) {
    const [a, b, c, d, tx, ty] = matrixMatch[1].split(",").map((v) => parseFloat(v.trim()));
    result.x = result.x ?? tx;
    result.y = result.y ?? ty;
    // Approximate scale from matrix
    const scaleX = Math.hypot(a, b);
    const scaleY = Math.hypot(c, d);
    result.scale = result.scale ?? Math.max(scaleX, scaleY);
    const rotation = Math.atan2(b, a) * (180 / Math.PI);
    result.rotate = result.rotate ?? rotation;
  }

  // matrix3d(m11, m12, m13, m14, m21, m22, m23, m24, m31, m32, m33, m34, m41, m42, m43, m44)
  const matrix3dMatch = transform.match(/matrix3d\(([^)]+)\)/);
  if (matrix3dMatch) {
    const m = matrix3dMatch[1].split(",").map((v) => parseFloat(v.trim()));
    const tx = m[12];
    const ty = m[13];
    const tz = m[14];
    result.x = result.x ?? tx;
    result.y = result.y ?? ty;
    result.z = result.z ?? tz;
    // Approximate scale from matrix3d
    const scaleX = Math.hypot(m[0], m[1], m[2]);
    const scaleY = Math.hypot(m[4], m[5], m[6]);
    // const scaleZ = Math.hypot(m[8], m[9], m[10]);
    const approxScale = Math.max(scaleX, scaleY);
    if (!Number.isNaN(approxScale) && approxScale > 0) {
      result.scale = result.scale ?? approxScale;
    }
    // Yaw/pitch/roll extraction is complex; approximate 2D rotation from XY plane
    const rotation = Math.atan2(m[1], m[0]) * (180 / Math.PI);
    if (!Number.isNaN(rotation)) {
      result.rotate = result.rotate ?? rotation;
    }
  }

  return result;
}

// Parses an inset() clip-path string and converts its sides to percentages.
// Accepts values in % or px; px are converted based on element size.
function parseInsetClipToPercents(clipPath: string, elementWidth: number, elementHeight: number): { top: number; right: number; bottom: number; left: number } | undefined {
  if (!clipPath) return undefined;
  const match = clipPath.match(/inset\(([^)]+)\)/i);
  if (!match) return undefined;
  const parts = match[1]
    .split(/[\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length < 1) return undefined;

  // Normalize to 4 values: top, right, bottom, left
  const values = normalizeInsetParts(parts);
  // Convert to percentages
  const [top, right, bottom, left] = values.map((v, idx) => {
    if (v.endsWith("%")) return parseFloat(v);
    if (v.endsWith("px")) {
      const px = parseFloat(v);
      const basis = idx % 2 === 0 ? elementHeight : elementWidth; // top/bottom use height; right/left use width
      return (px / (basis || 1)) * 100;
    }
    // unitless treated as px
    const px = parseFloat(v);
    const basis = idx % 2 === 0 ? elementHeight : elementWidth;
    return (px / (basis || 1)) * 100;
  });
  return { top, right, bottom, left };
}

function normalizeInsetParts(parts: string[]): string[] {
  const p = parts;
  if (p.length === 1) return [p[0], p[0], p[0], p[0]];
  if (p.length === 2) return [p[0], p[1], p[0], p[1]];
  if (p.length === 3) return [p[0], p[1], p[2], p[1]];
  return [p[0], p[1], p[2], p[3]];
}

// ✅ Parallax Module Features

//   Simple Usage

// [data-parallax] {  transform: translate3d(0, 0, 0); }

//   <div data-parallax="0.5">moves slower than scroll</div>
//   <div data-parallax="-0.3">moves opposite direction</div>

//   Advanced Usage

//   <div data-parallax="y:0.5">vertical parallax</div>
//   <div data-parallax="x:0.3">horizontal parallax</div>
//   <div data-parallax="x:0.3,y:0.5">both directions</div>
//   <div data-parallax="y:0.5,scale:0.1">with scaling</div>
//   <div data-parallax="y:0.5,rotate:45">with rotation</div>
//
